import { closestCenter, DndContext, DragEndEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DropzonePreview } from '../domain/previews';
import { PreviewItem } from './preview-item';
import { PreviewList as List } from './styles';

export interface PreviewListProps {
  previews: DropzonePreview[];
  size: number;
  showFileName?: boolean;
  sortable?: boolean;
  onRemove?: (index: number) => void;
  onReorder?: (from: number, to: number) => void;
  onOpen?: (index: number) => void;
}

/**
 * Ряд выбранных картинок.
 *
 * Перетаскивание включается пропом и стоит недёшево — сенсоры, замеры,
 * подписки, — поэтому без `sortable` список остаётся простой разметкой.
 */
export function PreviewList({ previews, size, showFileName, sortable, onRemove, onReorder, onOpen }: PreviewListProps) {
  if (!previews.length) return null;

  if (!sortable) {
    return (
      <List>
        {previews.map((preview, index) => (
          <PreviewItem
            key={preview.key}
            preview={preview}
            size={size}
            showFileName={showFileName}
            onRemove={onRemove && (() => onRemove(index))}
            onOpen={onOpen && (() => onOpen(index))}
          />
        ))}
      </List>
    );
  }

  return (
    <SortablePreviews
      previews={previews}
      size={size}
      showFileName={showFileName}
      onRemove={onRemove}
      onReorder={onReorder}
      onOpen={onOpen}
    />
  );
}

function SortablePreviews({ previews, size, showFileName, onRemove, onReorder, onOpen }: Omit<PreviewListProps, 'sortable'>) {
  const sensors = useSensors(
    // Порог в пять пикселей: без него обычный клик по миниатюре читается как
    // начало перетаскивания, и лайтбокс не открывается.
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const from = previews.findIndex((preview) => preview.key === active.id);
    const to = previews.findIndex((preview) => preview.key === over.id);

    if (from !== -1 && to !== -1) onReorder?.(from, to);
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={previews.map((preview) => preview.key)}>
        <List>
          {previews.map((preview, index) => (
            <SortablePreview
              key={preview.key}
              preview={preview}
              size={size}
              showFileName={showFileName}
              onRemove={onRemove && (() => onRemove(index))}
              onOpen={onOpen && (() => onOpen(index))}
            />
          ))}
        </List>
      </SortableContext>
    </DndContext>
  );
}

interface SortablePreviewProps {
  preview: DropzonePreview;
  size: number;
  showFileName?: boolean;
  onRemove?: () => void;
  onOpen?: () => void;
}

function SortablePreview({ preview, size, showFileName, onRemove, onOpen }: SortablePreviewProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: preview.key,
    transition: { duration: 500, easing: 'cubic-bezier(0.25, 1, 0.5, 1)' },
  });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
        touchAction: 'none',
        cursor: 'grab',
      }}
      {...attributes}
      {...listeners}
    >
      <PreviewItem preview={preview} size={size} showFileName={showFileName} onRemove={onRemove} onOpen={onOpen} />
    </div>
  );
}
